import { Editor } from '@tiptap/react';
import React, { useRef } from 'react';

import { HeadingSelector } from '../HeadingSelector/HeadingSelector';
import ImagePopup from '../ImagePopup/ImagePopup';
import LinkPopup from '../LinkPopup/LinkPopup';
import { ToolbarButton } from '../ToolbarButton/ToolbarButton';
import classes from './EditorToolbar.module.scss';

interface EditorToolbarProps {
	editor: Editor | null;
	currentHeading: string;
	showLinkPopup: boolean;
	showImagePopup: boolean;
	onBold: () => void;
	onItalic: () => void;
	onStrike: () => void;
	onHeading: (level: 1 | 2 | 3 | 4 | 5 | 6) => void;
	onParagraph: () => void;
	onBulletList: () => void;
	onOrderedList: () => void;
	onBlockquote: () => void;
	onLinkPopupToggle: (show: boolean) => void;
	onImagePopupToggle: (show: boolean) => void;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
	editor,
	currentHeading,
	showLinkPopup,
	showImagePopup,
	onBold,
	onItalic,
	onStrike,
	onHeading,
	onParagraph,
	onBulletList,
	onOrderedList,
	onBlockquote,
	onLinkPopupToggle,
	onImagePopupToggle,
}) => {
	const linkButtonRef = useRef<HTMLDivElement>(null);
	const imageButtonRef = useRef<HTMLDivElement>(null);

	return (
		<div className={classes.toolbar}>
			<HeadingSelector
				currentHeading={currentHeading}
				onHeadingChange={onHeading}
				onParagraph={onParagraph}
			/>

			<ToolbarButton onClick={onBold} title='Жирный' isActive={editor?.isActive('bold')}>
				<svg
					width='24px'
					height='24px'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path
						d='M8 12H12.5M8 12V5H12.5C14.433 5 16 6.567 16 8.5C16 10.433 14.433 12 12.5 12M8 12V19H13.5C15.433 19 17 17.433 17 15.5C17 13.567 15.433 12 13.5 12H12.5'
						stroke='currentColor'
						strokeWidth='3'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
				</svg>
			</ToolbarButton>

			<ToolbarButton onClick={onItalic} title='Курсив' isActive={editor?.isActive('italic')}>
				<svg
					width='24px'
					height='24px'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path
						d='M8 19H10M10 19H12M10 19L14 5M12 5H14M14 5H16'
						stroke='currentColor'
						strokeWidth='1.4'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
				</svg>
			</ToolbarButton>

			<ToolbarButton
				onClick={onStrike}
				isActive={editor?.isActive('strike')}
				title='Зачеркнутый'
			>
				<svg
					width='24px'
					height='24px'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path
						d='M12.0005 12.0001C12.8959 12.0001 13.7749 12.1925 14.5457 12.5571C14.8939 12.7218 15.2146 12.9192 15.5009 13.1437C15.8484 13.4162 16.1457 13.729 16.3822 14.0732C16.8136 14.7009 17.0263 15.4096 16.9982 16.1256C16.97 16.8416 16.702 17.5385 16.2222 18.1433C15.7424 18.7481 15.0684 19.2386 14.2705 19.5638C13.4727 19.889 12.5802 20.0373 11.6865 19.9923C10.7928 19.9473 9.93104 19.7108 9.19043 19.3082C8.44982 18.9055 7.85782 18.3514 7.47656 17.7032M12.0005 12.0001H4M12.0005 12.0001H20M16.5243 6.29718C16.143 5.649 15.5512 5.09462 14.8105 4.69197C14.0699 4.28932 13.2076 4.05287 12.314 4.00789C11.4203 3.96291 10.5278 4.11091 9.72998 4.43613C8.93213 4.76135 8.25812 5.25205 7.77832 5.85689C7.29852 6.46173 7.03057 7.15885 7.00244 7.87485C6.9942 8.08463 7.00669 8.29345 7.03924 8.50014'
						stroke='currentColor'
						strokeWidth='1.4'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
				</svg>
			</ToolbarButton>

			<div className={classes.relative} ref={linkButtonRef}>
				<ToolbarButton
					onClick={() => onLinkPopupToggle(true)}
					title='Ссылка'
					isActive={editor?.isActive('link')}
				>
					<svg
						width='24px'
						height='24px'
						viewBox='0 0 24 24'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							d='M9.1718 14.8288L14.8287 9.17192M7.05086 11.293L5.63664 12.7072C4.07455 14.2693 4.07409 16.8022 5.63619 18.3643C7.19829 19.9264 9.7317 19.9259 11.2938 18.3638L12.7065 16.9498M11.2929 7.05L12.7071 5.63579C14.2692 4.07369 16.8016 4.07397 18.3637 5.63607C19.9258 7.19816 19.9257 9.73085 18.3636 11.2929L16.9501 12.7071'
							stroke='currentColor'
							strokeWidth='1.4'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</svg>
				</ToolbarButton>
				<LinkPopup
					editor={editor}
					isOpen={showLinkPopup}
					onClose={() => onLinkPopupToggle(false)}
					buttonRef={linkButtonRef}
				/>
			</div>
			<ToolbarButton
				onClick={onBulletList}
				isActive={editor?.isActive('bulletList')}
				title='Маркированный список'
			>
				<svg
					width='24px'
					height='24px'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path
						d='M9 17H19M9 12H19M9 7H19M5.00195 17V17.002L5 17.002V17H5.00195ZM5.00195 12V12.002L5 12.002V12H5.00195ZM5.00195 7V7.002L5 7.00195V7H5.00195Z'
						stroke='currentColor'
						strokeWidth='1.4'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
				</svg>
			</ToolbarButton>
			<ToolbarButton
				onClick={onOrderedList}
				isActive={editor?.isActive('orderedList')}
				title='Нумерованный список'
			>
				<svg
					width='24px'
					height='24px'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path
						d='M10 17H20M4 15.6853V15.5C4 14.6716 4.67157 14 5.5 14H5.54054C6.34658 14 7.00021 14.6534 7.00021 15.4595C7.00021 15.8103 6.8862 16.1519 6.67568 16.4326L4 20.0002L7 20M10 12H20M10 7H20M4 5L6 4V10'
						stroke='currentColor'
						strokeWidth='1.4'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
				</svg>
			</ToolbarButton>
			<ToolbarButton
				onClick={onBlockquote}
				isActive={editor?.isActive('blockquote')}
				title='Цитата'
			>
				<svg
					width='24px'
					height='24px'
					viewBox='0 0 24 24'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<path
						d='M14 12V15.4C14 15.9601 14 16.2399 14.109 16.4538C14.2049 16.642 14.3577 16.7952 14.5459 16.8911C14.7596 17 15.0395 17 15.5985 17H17.4015C17.9605 17 18.24 17 18.4537 16.8911C18.6419 16.7952 18.7952 16.6419 18.8911 16.4537C19 16.24 19 15.9605 19 15.4015V13.5985C19 13.0395 19 12.7596 18.8911 12.5459C18.7952 12.3577 18.642 12.2049 18.4538 12.109C18.2399 12 17.9601 12 17.4 12H14ZM14 12V10C14 8.34315 15.3431 7 17 7M5 12V15.4C5 15.9601 5 16.2399 5.10899 16.4538C5.20487 16.642 5.35774 16.7952 5.5459 16.8911C5.7596 17 6.0395 17 6.59845 17H8.40154C8.9605 17 9.23999 17 9.4537 16.8911C9.64186 16.7952 9.79524 16.6419 9.89111 16.4537C10 16.24 10 15.9605 10 15.4015V13.5985C10 13.0395 10 12.7596 9.89111 12.5459C9.79524 12.3577 9.64196 12.2049 9.45379 12.109C9.23988 12 8.96005 12 8.4 12H5ZM5 12V10C5 8.34315 6.34315 7 8 7'
						stroke='currentColor'
						strokeWidth='1.4'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>
				</svg>
			</ToolbarButton>
			<div className={classes.relative} ref={imageButtonRef}>
				<ToolbarButton onClick={() => onImagePopupToggle(true)} title='Изображение'>
					<svg
						width='24px'
						height='24px'
						viewBox='0 0 24 24'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							d='M3.00005 18.0001C3 17.9355 3 17.8689 3 17.8002V6.2002C3 5.08009 3 4.51962 3.21799 4.0918C3.40973 3.71547 3.71547 3.40973 4.0918 3.21799C4.51962 3 5.08009 3 6.2002 3H17.8002C18.9203 3 19.4801 3 19.9079 3.21799C20.2842 3.40973 20.5905 3.71547 20.7822 4.0918C21 4.5192 21 5.07899 21 6.19691V17.8031C21 18.2881 21 18.6679 20.9822 18.9774M3.00005 18.0001C3.00082 18.9884 3.01337 19.5058 3.21799 19.9074C3.40973 20.2837 3.71547 20.5905 4.0918 20.7822C4.5192 21 5.07899 21 6.19691 21H17.8036C18.9215 21 19.4805 21 19.9079 20.7822C20.2842 20.5905 20.5905 20.2837 20.7822 19.9074C20.9055 19.6654 20.959 19.3813 20.9822 18.9774M3.00005 18.0001L7.76798 12.4375L7.76939 12.436C8.19227 11.9426 8.40406 11.6955 8.65527 11.6064C8.87594 11.5282 9.11686 11.53 9.33643 11.6113C9.58664 11.704 9.79506 11.9539 10.2119 12.4541L12.8831 15.6595C13.269 16.1226 13.463 16.3554 13.6986 16.4489C13.9065 16.5313 14.1357 16.5406 14.3501 16.4773C14.5942 16.4053 14.8091 16.1904 15.2388 15.7607L15.7358 15.2637C16.1733 14.8262 16.3921 14.6076 16.6397 14.5361C16.8571 14.4734 17.0896 14.4869 17.2988 14.5732C17.537 14.6716 17.7302 14.9124 18.1167 15.3955L20.9822 18.9774M20.9822 18.9774L21 18.9996M15 9C14.4477 9 14 8.55228 14 8C14 7.44772 14.4477 7 15 7C15.5523 7 16 7.44772 16 8C16 8.55228 15.5523 9 15 9Z'
							stroke='currentColor'
							strokeWidth='1.4'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</svg>
				</ToolbarButton>
				<ImagePopup
					editor={editor}
					isOpen={showImagePopup}
					onClose={() => onImagePopupToggle(false)}
					buttonRef={imageButtonRef}
				/>
			</div>
		</div>
	);
};
